import type {
  BulkTransferContactsPayload,
  BulkTransferContactsResult,
  Contact,
  ContactEpisode,
  ContactEpisodeOutcome,
  ContactFollowUp,
  ContactOriginDetails,
  ContactOutcomeType,
  ContactTouchType,
  CreateContactPayload,
  PatientMrnSyncResult,
  PreviewTeamTransferPayload,
  PreviewTeamTransferResult,
  UpdateContactPayload,
  BulkTagContactsPayload,
  BulkNoteContactsPayload,
  BulkCloseContactsPayload,
  BulkContactsActionResult,
} from "@/features/contact/types";
import type { ContactOrigin } from "@/features/contact/constants/origin";
import type { ContactFilterQuery } from "@/features/inbox/types/filter";
import type { LeadSourceGroup } from "@/features/reports/leads/types";
import type { Activity, CreateActivityPayload } from "@/types/activity";

import { DateTime } from "luxon";

import {
  createAvatarUrl,
  createId,
  createPastTimestamp,
} from "../generators/base";
import { useMockStore } from "../store";

import {
  followUpHistoryMock,
  rememberCompletedFollowUpMock,
} from "./follow-up-credit";
import { seesContactMock } from "./role";
import { tagActionMock } from "./tag";
import { applySort, type FilterParams } from "./types";
import { CLINICSOFT_MOCK_INTEGRATION } from "./integration";

import { OPT_OUT_SOURCE } from "@/features/contact/types";
import { getChannelPlatformName } from "@/utils/channel";
import { getContactFullName } from "@/utils/contact";
import { PATIENT_MRN_FIELD_KEY } from "@/features/contact/constants/patient";
import { evaluateQuery } from "@/features/inbox/helpers/conversation-match";
import { PATIENT_MRN_SYNC_STATUS } from "@/features/contact/types";

interface ContactFilter extends FilterParams {
  /** The saved/ad-hoc rule group: a segment, a sidebar row or the Filters drawer. */
  filter?: ContactFilterQuery;
  lifecycleId?: string;
  assignedTo?: string;
  status?: string;
  tags?: string[];
  blocked?: boolean;
  followUpRequired?: boolean;
  followUpDue?: boolean;
}

const isFollowUpDue = (followUp: ContactFollowUp | null): boolean => {
  if (!followUp?.dueAt) return false;

  return new Date(followUp.dueAt).getTime() <= Date.now();
};

/**
 * Get all contacts with filtering
 */
export function getContactsMock(filters: ContactFilter = {}): Contact[] {
  const store = useMockStore.getState();
  let contacts = store.contacts.filter(seesContactMock);

  // Filter by lifecycle
  if (filters.lifecycleId) {
    contacts = contacts.filter((c) => c.lifecycleId === filters.lifecycleId);
  }

  // Filter by assigned user
  if (filters.assignedTo) {
    contacts = contacts.filter((c) => c.assignedUserId === filters.assignedTo);
  }

  // Filter by status
  if (filters.status) {
    contacts = contacts.filter((c) => c.status === filters.status);
  }

  // Filter by tags
  // The saved query behind a segment, the sidebar rows and the Filters drawer.
  // Reuses the client-side matcher the cache reconciler runs, so the mock and the
  // real list agree on membership; `indeterminate` keeps the row, since the server
  // is what settles undecidable fields.
  if (filters.filter && filters.filter.rules.length > 0) {
    contacts = contacts.filter(
      (c) => evaluateQuery(c, filters.filter) !== "no-match",
    );
  }

  if (filters.tags && filters.tags.length > 0) {
    contacts = contacts.filter((c) =>
      filters.tags?.some((tagId) => c.tags.includes(tagId)),
    );
  }

  // Filter by blocked
  if (filters.blocked !== undefined) {
    contacts = contacts.filter((c) => c.blocked === filters.blocked);
  }

  // Filter by follow-up presence / due-state
  if (filters.followUpRequired !== undefined) {
    contacts = contacts.filter(
      (c) => Boolean(c.followUp) === filters.followUpRequired,
    );
  }

  if (filters.followUpDue !== undefined) {
    contacts = contacts.filter(
      (c) => isFollowUpDue(c.followUp) === filters.followUpDue,
    );
  }

  // Apply search
  const search = filters.search?.trim().toLowerCase();

  if (search) {
    contacts = contacts.filter((contact) =>
      [getContactFullName(contact), contact.email, contact.phone].some(
        (value) => value?.toLowerCase().includes(search),
      ),
    );
  }

  // Apply sort
  contacts = applySort(
    contacts,
    filters.sortBy ?? "createdAt",
    filters.sortOrder ?? "desc",
  );

  return contacts;
}

/**
 * Get contacts by lifecycle
 */
export function getContactsByLifecycleMock(lifecycleId: string): Contact[] {
  const store = useMockStore.getState();

  return store.contacts.filter((c) => c.lifecycleId === lifecycleId);
}

/**
 * Get a single contact by ID
 */
export function getContactMock(contactId: string): Contact | undefined {
  const store = useMockStore.getState();

  const contact = store.contacts.find((c) => c.id === contactId);

  return contact && seesContactMock(contact) ? contact : undefined;
}

const EPISODE_GROUP: Record<ContactOrigin, LeadSourceGroup> = {
  ad: "ad",
  organic: "direct",
  referral: "referral",
  manual: "manual",
  api: "api",
  import: "import",
  crm: "crm",
};

const OPENER_TOUCH: Partial<Record<LeadSourceGroup, ContactTouchType>> = {
  ad: "ad_click",
  referral: "referral",
  manual: "manual",
  api: "api",
  import: "import",
  crm: "crm_sync",
};

const hoursAgo = (hours: number) =>
  DateTime.now().minus({ hours }).toISO() ?? "";

const appointmentOutcome = (
  type: ContactOutcomeType,
  entityId: string,
  occurredAt: string,
): ContactEpisodeOutcome => ({
  type,
  entityType: "appointment",
  entityId,
  occurredAt,
  amount: null,
});

/** Every third contact came back through a broadcast after a win, the next booked and never showed; the rest are one open episode. */
const episodesOf = (contact: Contact, index: number): ContactEpisode[] => {
  const group = contact.origin ? EPISODE_GROUP[contact.origin] : "unknown";
  const platform =
    group === "ad"
      ? "meta"
      : group === "direct"
        ? (contact.channels[0]?.type ?? null)
        : null;
  const first: ContactEpisode = {
    sequence: 1,
    startedAt: contact.createdAt,
    leadAt: contact.createdAt,
    isNew: true,
    group,
    platform,
    touches: [
      {
        type: OPENER_TOUCH[group] ?? "inbound_message",
        occurredAt: contact.createdAt,
        platform,
        detail: null,
        owner: null,
        userName: null,
      },
    ],
    outcomes: [],
    endedAt: null,
    endedKind: null,
  };
  const appointmentId = `appointment-${contact.id}`;

  if (index % 3 === 1) {
    return [
      {
        ...first,
        outcomes: [
          appointmentOutcome("booked", appointmentId, contact.createdAt),
          appointmentOutcome("no_show", appointmentId, hoursAgo(20)),
        ],
        endedAt: hoursAgo(4),
        endedKind: "lost",
      },
    ];
  }
  if (index % 3 !== 0) return [first];

  return [
    {
      sequence: 2,
      startedAt: hoursAgo(30),
      leadAt: hoursAgo(30),
      isNew: false,
      group: "broadcast",
      platform: "whatsapp",
      touches: [
        {
          type: "broadcast_reply",
          occurredAt: hoursAgo(30),
          platform: "whatsapp",
          detail: { templateId: "template-recall-6-months" },
          owner: null,
          userName: null,
        },
      ],
      outcomes: [
        appointmentOutcome("booked", `${appointmentId}-2`, hoursAgo(28)),
      ],
      endedAt: null,
      endedKind: null,
    },
    {
      ...first,
      outcomes: [
        appointmentOutcome("booked", appointmentId, contact.createdAt),
        appointmentOutcome("attended", appointmentId, hoursAgo(72)),
        {
          type: "paid",
          entityType: "receipt",
          entityId: `receipt-${contact.id}`,
          occurredAt: hoursAgo(72),
          amount: 450,
        },
      ],
      endedAt: hoursAgo(70),
      endedKind: "won",
    },
  ];
};

/** A contact's origin, built from what the mock contact already carries. */
export function getContactOriginMock(
  contactId: string,
): ContactOriginDetails | undefined {
  const contact = getContactMock(contactId);

  if (!contact) return undefined;

  const index = useMockStore.getState().contacts.indexOf(contact);
  const user = contact.assignedUser
    ? {
        id: contact.assignedUser.id,
        name: `${contact.assignedUser.firstName} ${contact.assignedUser.lastName ?? ""}`.trim(),
        imageUrl: contact.assignedUser.imageUrl ?? null,
      }
    : null;
  const channel = contact.channels[0];

  return {
    origin: contact.origin,
    createdAt: contact.createdAt,
    sourceDetail:
      contact.referredByName ??
      contact.lastAd?.name ??
      channel?.identifier ??
      null,
    createdBy: contact.origin === "api" ? "api" : user ? "user" : "system",
    createdByUser: user,
    createdDescription: null,
    import:
      contact.origin === "import"
        ? {
            id: `upload-${contact.id}`,
            name: "patients-september.csv",
            totalCount: 412,
            createdAt: contact.createdAt,
          }
        : null,
    firstChannel: channel
      ? {
          type: channel.type,
          name: getChannelPlatformName(channel.type),
          firstMessageAt: contact.createdAt,
        }
      : null,
    episodes: episodesOf(contact, index),
  };
}

/**
 * Create a new contact
 */
export function createContactMock(payload: CreateContactPayload): Contact {
  const store = useMockStore.getState();
  const timestamp = createPastTimestamp(0);

  const newContact: Contact = {
    id: createId(),
    firstName: payload.firstName ?? null,
    lastName: payload.lastName ?? null,
    username: null,
    email: payload.email ?? null,
    phone: payload.phone ?? null,
    address: payload.address ?? null,
    language: "en",
    country: null,
    imageUrl: createAvatarUrl(),
    blocked: false,
    lifecycleId: payload.lifecycleId ?? store.lifecycles[0]?.id ?? null,
    sortOrder: store.contacts.length,
    assignedUserId: null,
    lastMessageProcessedAt: timestamp,
    lastBumpedAt: timestamp,
    lastMessageOwner: "contact",
    lastMessageId: createId(),
    createdAt: timestamp,
    updatedAt: timestamp,
    tags: payload.tags ?? [],
    treatmentIds: [],
    productIds: [],
    adIds: [],
    websiteIds: [],
    channels: [],
    // Mirrors the backend derivation: naming a referrer is what makes it a referral.
    origin: payload.referredByName ? "referral" : "manual",
    referredByName: payload.referredByName ?? null,
    interactions: [],
    status: "open",
    snoozedUntil: null,
    followUp: null,
    unreadCount: 0,
    unreadMentionCount: 0,
    assignedUser: null,
    lastSummary: null,
    lastNote: null,
    lastNoteAt: null,
    lastAd: null,
    lastWebsiteVisit: null,
    value: "0.00",
    humanAgentRequired: false,
    knowledgeGapPending: false,
    aiPending: null,
    assignedTeamId: null,
    aiMode: "off",
    aiModeUntil: null,
    aiModeRevertTo: null,
  };

  store.addContact(newContact);

  return newContact;
}

/**
 * Update an existing contact
 */
export function updateContactMock(
  contactId: string,
  payload: UpdateContactPayload,
  completedFollowUp = false,
): Contact | undefined {
  const store = useMockStore.getState();
  const contact = store.contacts.find((c) => c.id === contactId);

  if (!contact) return undefined;

  const {
    value: rawValue,
    followUp: followUpPayload,
    closingNote,
    optedOut,
    ...modePayload
  } = payload;
  // As the backend: a mode change by hand clears the pause timer.
  const restPayload =
    "aiMode" in modePayload && !("aiModeUntil" in modePayload)
      ? { ...modePayload, aiModeUntil: null, aiModeRevertTo: null }
      : modePayload;

  let nextFollowUp: ContactFollowUp | null | undefined;

  if (followUpPayload === null) {
    nextFollowUp = null;
    if (completedFollowUp && contact.followUp) {
      rememberCompletedFollowUpMock(contactId, contact.followUp);
    }
  } else if (followUpPayload) {
    nextFollowUp = {
      id: contact.followUp?.id ?? createId(),
      reason: followUpPayload.reason ?? contact.followUp?.reason ?? null,
      dueAt:
        followUpPayload.dueAt ??
        contact.followUp?.dueAt ??
        new Date().toISOString(),
      source: contact.followUp?.source ?? "user",
      createdByUserId: store.currentUserId,
      reminderSentAt: contact.followUp?.reminderSentAt ?? null,
      createdAt: new Date().toISOString(),
    };
  }

  const updates: Partial<Contact> = {
    ...restPayload,
    ...(nextFollowUp !== undefined && { followUp: nextFollowUp }),
    ...(rawValue !== undefined && { value: rawValue.toFixed(2) }),
    ...(optedOut !== undefined && {
      optedOutAt: optedOut ? new Date().toISOString() : null,
      optOutSource: optedOut ? OPT_OUT_SOURCE.TEAMMATE : null,
    }),
    // Merged, never replaced - the backend writes these as a jsonb concat, and a
    // per-field save names only the key it changed.
    ...(restPayload.dynamicValues && {
      dynamicValues: {
        ...contact.dynamicValues,
        ...restPayload.dynamicValues,
      },
    }),
    updatedAt: new Date().toISOString(),
  };

  store.updateContact(contactId, updates);

  if (payload.status && payload.status !== contact.status) {
    const note = store.closingNotes.find(
      (item) => item.id === closingNote?.topicId,
    );

    store.addActivity(contactId, {
      id: createId(),
      contactId,
      userId: store.currentUserId,
      mentioned: [],
      owner: "user",
      createdAt: updates.updatedAt!,
      type: "conversation_status_change",
      payload: {
        status: payload.status,
        ...(closingNote && {
          closingNote: {
            ...closingNote,
            topicName: closingNote.topicName || note?.category || "",
            fieldValues: closingNote.fieldValues?.map((entry) => {
              const field = note?.fields.find(
                (item) => item.id === entry.fieldId,
              );

              return {
                ...entry,
                fieldName: field?.name ?? "Field",
                type: field?.type ?? "string",
              };
            }),
          },
        }),
      },
    });
  }

  if (optedOut !== undefined && optedOut !== Boolean(contact.optedOutAt)) {
    store.addActivity(contactId, {
      id: createId(),
      contactId,
      userId: store.currentUserId,
      mentioned: [],
      owner: "user",
      createdAt: updates.updatedAt!,
      type: optedOut ? "opt_out" : "opt_in",
      payload: { source: OPT_OUT_SOURCE.TEAMMATE, userId: store.currentUserId },
    });
  }

  if (nextFollowUp !== undefined && (nextFollowUp || contact.followUp)) {
    const followUp = nextFollowUp ?? contact.followUp!;

    store.addActivity(contactId, {
      id: createId(),
      contactId,
      userId: store.currentUserId,
      mentioned: [],
      owner: "user",
      createdAt: updates.updatedAt!,
      type: nextFollowUp
        ? contact.followUp
          ? "follow_up_updated"
          : "follow_up_set"
        : completedFollowUp
          ? "follow_up_completed"
          : "follow_up_cleared",
      payload: {
        followUpAt: followUp.dueAt,
        followUpReason: followUp.reason,
        source: followUp.source,
      },
    });
  }

  return { ...contact, ...updates };
}

/**
 * Pretend to go and ask the CRM for this patient's MRN.
 *
 * Covers all three outcomes the real integration produces rather than only the
 * happy one: a contact with no number alternates between "the clinic assigned
 * one just now" and "still not assigned", because the latter is the case for a
 * third of patients and is the one the button exists for - a demo that always
 * finds a number would hide it.
 */
export function syncPatientMrnMock(contactId: string): PatientMrnSyncResult {
  const store = useMockStore.getState();
  const contact = store.contacts.find((c) => c.id === contactId);
  const integrationName = CLINICSOFT_MOCK_INTEGRATION.name;

  // The real endpoint 404s on an unknown contact, which surfaces as the error
  // alert rather than as a status.
  if (!contact) throw new Error("Contact not found");

  const existing = contact.dynamicValues?.[PATIENT_MRN_FIELD_KEY];

  if (existing !== null && existing !== undefined && String(existing).trim()) {
    return {
      status: PATIENT_MRN_SYNC_STATUS.UNCHANGED,
      mrn: String(existing),
      integrationName,
    };
  }

  // Deterministic rather than random so a screenshot of a given contact is
  // stable, while both outcomes stay reachable across the seeded set.
  const assignsNow = contact.id.charCodeAt(contact.id.length - 1) % 2 === 0;

  if (!assignsNow) {
    return {
      status: PATIENT_MRN_SYNC_STATUS.NOT_ASSIGNED,
      mrn: null,
      integrationName,
    };
  }

  const mrn = String(7000 + (contact.id.charCodeAt(0) % 900));

  store.updateContact(contactId, {
    dynamicValues: { ...contact.dynamicValues, [PATIENT_MRN_FIELD_KEY]: mrn },
  });

  return { status: PATIENT_MRN_SYNC_STATUS.SYNCED, mrn, integrationName };
}

const resolveTeamAssignments = (
  payload: PreviewTeamTransferPayload,
): PreviewTeamTransferResult => {
  const store = useMockStore.getState();
  const { contactIds, assignedTeamId } = payload;
  const team =
    store.teams.find((candidate) => candidate.id === assignedTeamId) ?? null;
  const teamMemberIds = team?.members.map((member) => member.id).sort() ?? [];
  const onlineMemberIds = teamMemberIds.filter(
    (userId) =>
      store.users.find((user) => user.id === userId)?.presence?.status ===
      "online",
  );
  const routingPool =
    team?.assignmentStrategy === "online" && onlineMemberIds.length > 0
      ? onlineMemberIds
      : teamMemberIds;
  const openAssignmentCounts = new Map(
    routingPool.map((userId) => [
      userId,
      store.contacts.filter(
        (contact) =>
          contact.assignedUserId === userId && contact.status === "open",
      ).length,
    ]),
  );
  let roundRobinIndex = 0;

  const pickTeamMember = (): string | null => {
    if (routingPool.length === 0) return null;

    if (team?.assignmentStrategy === "round_robin") {
      const picked = routingPool[roundRobinIndex % routingPool.length];

      roundRobinIndex += 1;

      return picked;
    }

    const picked = [...routingPool].sort((left, right) => {
      const countDifference =
        (openAssignmentCounts.get(left) ?? 0) -
        (openAssignmentCounts.get(right) ?? 0);

      return countDifference || left.localeCompare(right);
    })[0];

    openAssignmentCounts.set(
      picked,
      (openAssignmentCounts.get(picked) ?? 0) + 1,
    );

    return picked;
  };

  return {
    assignments: contactIds
      .filter((contactId) =>
        store.contacts.some((contact) => contact.id === contactId),
      )
      .map((contactId) => ({
        contactId,
        assignedUserId: pickTeamMember(),
        assignedTeamId,
      })),
  };
};

export function previewTeamTransferMock(
  payload: PreviewTeamTransferPayload,
): PreviewTeamTransferResult {
  return resolveTeamAssignments(payload);
}

/**
 * Bulk-transfer contacts to a teammate and/or team.
 *
 * A contact counts as "transferred" when its assignee changes; when only its
 * team changes it counts as "team updated". A contact already assigned to the
 * target is skipped, mirroring the backend's no-op behaviour.
 */
export function bulkTransferContactsMock(
  payload: BulkTransferContactsPayload,
): BulkTransferContactsResult {
  const store = useMockStore.getState();
  const { contactIds, assignedUserId, assignedTeamId } = payload;
  const recommendedByContactId = new Map(
    assignedTeamId && !assignedUserId
      ? resolveTeamAssignments({ contactIds, assignedTeamId }).assignments.map(
          (assignment) => [assignment.contactId, assignment.assignedUserId],
        )
      : [],
  );

  const transferredContactIds: string[] = [];
  const teamUpdatedContactIds: string[] = [];
  const assignments: BulkTransferContactsResult["assignments"] = [];

  for (const contactId of contactIds) {
    const contact = store.contacts.find((c) => c.id === contactId);

    if (!contact) continue;

    const resolvedUserId =
      assignedUserId ?? recommendedByContactId.get(contactId) ?? null;
    const assignedUser = resolvedUserId
      ? (store.users.find((user) => user.id === resolvedUserId) ?? null)
      : null;
    const userChanged = contact.assignedUserId !== resolvedUserId;
    const teamChanged =
      Boolean(assignedTeamId) && contact.assignedTeamId !== assignedTeamId;

    if (userChanged || teamChanged) {
      store.updateContact(contactId, {
        assignedUserId: resolvedUserId,
        assignedUser,
        ...(assignedTeamId ? { assignedTeamId } : {}),
        updatedAt: new Date().toISOString(),
      });

      if (userChanged) {
        transferredContactIds.push(contactId);
      } else {
        teamUpdatedContactIds.push(contactId);
      }
    }

    assignments.push({
      contactId,
      assignedUserId: resolvedUserId,
      assignedTeamId: assignedTeamId ?? contact.assignedTeamId,
    });
  }

  return { transferredContactIds, teamUpdatedContactIds, assignments };
}

/**
 * Delete a contact
 */
export function deleteContactMock(contactId: string): boolean {
  const store = useMockStore.getState();
  const exists = store.contacts.some((c) => c.id === contactId);

  if (exists) {
    store.deleteContact(contactId);

    return true;
  }

  return false;
}

export function createContactActivityMock(
  data: CreateActivityPayload,
): Activity {
  const store = useMockStore.getState();

  if (!store.contacts.some((contact) => contact.id === data.contactId))
    throw new Error("Contact not found");
  const activity = {
    ...data,
    id: data.id ?? createId(),
    userId: store.currentUserId,
    owner: "user",
    createdAt: new Date().toISOString(),
  } as Activity;

  store.addActivity(data.contactId, activity);
  if (activity.type === "note") {
    store.updateContact(data.contactId, {
      lastNote: activity.payload.text,
      lastNoteAt: activity.createdAt,
    });
  }

  return activity;
}

export function bulkTagContactsMock(
  payload: BulkTagContactsPayload,
): BulkContactsActionResult {
  tagActionMock("attach", payload.contactIds, payload.addTagIds ?? []);
  tagActionMock("detach", payload.contactIds, payload.removeTagIds ?? []);

  return {
    affectedContactIds: useMockStore
      .getState()
      .contacts.filter((contact) => payload.contactIds.includes(contact.id))
      .map((contact) => contact.id),
  };
}

export function bulkNoteContactsMock(
  payload: BulkNoteContactsPayload,
): BulkContactsActionResult {
  if (!payload.text.trim()) throw new Error("Enter a note");
  const contacts = useMockStore
    .getState()
    .contacts.filter((contact) => payload.contactIds.includes(contact.id));

  for (const contact of contacts) {
    createContactActivityMock({
      contactId: contact.id,
      type: "note",
      payload: { text: payload.text },
      mentioned: [],
    });
  }

  return { affectedContactIds: contacts.map((contact) => contact.id) };
}

export function bulkCloseContactsMock(
  payload: BulkCloseContactsPayload,
): BulkContactsActionResult {
  const contacts = useMockStore
    .getState()
    .contacts.filter(
      (contact) =>
        payload.contactIds.includes(contact.id) && contact.status !== "closed",
    );

  for (const contact of contacts) {
    updateContactMock(
      contact.id,
      {
        id: contact.id,
        status: "closed",
        closingNote: payload.closingNote,
        ...(payload.completeFollowUps ? { followUp: null } : {}),
      },
      payload.completeFollowUps,
    );
  }

  return { affectedContactIds: contacts.map((contact) => contact.id) };
}

/**
 * Mock follow-up history. The store only holds each contact's active follow-up,
 * so this returns that row when present; older, closed follow-ups come from the
 * activity log in mock mode exactly as they do for pre-release data.
 */
export function getFollowUpHistoryMock(contactId: string): ContactFollowUp[] {
  return followUpHistoryMock(contactId);
}
